// Okno Záznam: pole podle kontraktu, zobrazení ↔ úprava ve stejném okně, uložení s kontrolou verze,
// koncepty (automatické ukládání rozpracovaného formuláře), podmínky viditelnosti.
import { action, core, ApiError } from '../api';
import type { RecordDescriptor, CommandDef, FieldDef } from '../contract';
import { h, debounce } from '../dom';
import { t, tk, fmtDateTime } from '../i18n';
import { icon } from '../icons';
import { events } from '../events';
import { IwRibbon, type RibbonButton } from '../components/ribbon';
import { IwCommandBar, headButton, fillHref, barMenuItem, type BarItem } from '../components/commandbar';
import { IwMenu, type MenuItem } from '../components/menu';
import { IwTabs } from '../components/misc';
import { FieldControl, evalCondition } from '../components/fields';
import { initials } from '../components/cards';
import { confirm, confirmDiscard } from '../components/dialog';
import { toast, toastError } from '../components/toast';
import { aiSuggest } from '../components/ai';
import type { IwWindow, WindowView } from '../wm/window';
import type { WindowManager } from '../wm/manager';
import type { ViewHost } from './factory';
import { createView } from './factory';
import { loader, errorState } from '../components/misc';
import type { WindowDescriptor } from '../contract';

export class RecordView implements WindowView {
  readonly el: HTMLElement;
  private ribbon = new IwRibbon();
  private tabs = new IwTabs();
  private fields = new Map<string, FieldControl>();
  private record: Record<string, unknown>;
  private edit: boolean;
  private dirty = false;
  private saving = false;
  private id: string | null;
  private draftKey: string;
  private unsubscribe: (() => void) | null = null;
  private saveDraft = debounce(() => void this.persistDraft(), 2000);
  /** Lišta „obnoveny neuložené změny … Zahodit“ nad formulářem (jen když se vrátil rozpracovaný stav). */
  private notice: HTMLElement | null = null;
  /** ovládání 'bar' (koncepce A): lišta příkazů v záhlaví okna, nebo v podokně detailu seznamu (pane) */
  private bar: IwCommandBar | null = null;
  private pane: boolean;
  private cardEl = h('div', { class: 'record-card' });
  private ro: ResizeObserver | null = null;
  /** detail v podokně: uložený koncept se nabídne, do úprav až na pokyn (náhled nesmí skočit do formuláře) */
  private pendingDraft: Record<string, unknown> | null = null;

  constructor(
    private desc: RecordDescriptor,
    private win: IwWindow,
    private wm: WindowManager,
    private host: ViewHost,
  ) {
    this.record = { ...desc.record };
    this.id = typeof desc.params['id'] === 'string' ? (desc.params['id'] as string) : null;
    // detail v podokně je náhled: i formulář, který jinak začíná v úpravách (účet, nastavení), nejdřív jen ukáže
    this.edit = desc.edit && !(host.pane === true && !desc.isNew);
    this.draftKey = `${desc.app}/${desc.window}/${this.id ?? 'new'}`;
    this.pane = host.pane === true;
    // lišta v záhlaví jen u samostatného okna (vložený záznam v konzoli / záložce nesmí převzít záhlaví hostitele)
    if ((desc.chrome === 'bar' && host.inline !== true) || this.pane) {
      this.bar = new IwCommandBar();
      this.el = h('div', { class: 'view record-view chrome-bar hide-empty' + (this.pane ? ' in-pane' : '') }, h('div', { class: 'record-body' }, this.cardEl, this.tabs));
    } else {
      this.el = h('div', { class: 'view record-view' }, this.ribbon, h('div', { class: 'record-body' }, this.tabs));
    }
    if (this.bar && desc.card) {
      this.el.classList.add('has-card');
      this.ro = new ResizeObserver(() => this.el.classList.toggle('card-compact', this.el.clientHeight > 0 && this.el.clientHeight < 500));
      this.ro.observe(this.el);
    }
    this.build();
    this.buildRibbon();
    this.applyEdit();
    void this.loadDraft();
    if (this.id && desc.channels.length) {
      this.unsubscribe = events.subscribe(desc.channels, (evs) => {
        if (evs.some((e) => e.rid === this.id) && !this.dirty) void this.reload();
        else if (evs.some((e) => e.rid === this.id) && this.dirty) toast(t('core.err.conflict'), 'warn');
      });
    }
    if (!this.pane) this.win.setTitle(this.titleText(), desc.icon);
  }

  private setWinTitle(): void {
    if (!this.pane) this.win.setTitle(this.titleText(), this.desc.icon);
  }

  /** Vizitka v režimu čtení: fotka / iniciály, nadpis, podtitul a rychlé akce (Zavolat, Napsat…). */
  private renderCard(): void {
    const c = this.desc.card;
    this.cardEl.hidden = !c || this.edit || !this.id;
    if (!c || this.cardEl.hidden) return;
    const title = String(this.record[c.title] ?? '');
    const img = c.image ? this.record[c.image] : null;
    const ini = c.media === 'initials' ? initials(title) : null; // iniciály jen u osob, jinak ikona okna
    const quick = this.desc.commands.filter((x) => x.quick && this.allowedCmd(x)).map((x) => {
      const b = h('button', { type: 'button', class: 'quick-act', title: tk(x.label) }, icon(x.icon ?? 'check'), h('span', null, tk(x.label))) as HTMLButtonElement;
      b.addEventListener('click', () => void this.execute(x));
      return b;
    });
    const sub = c.subtitle ? this.record[c.subtitle] : null;
    this.cardEl.replaceChildren(
      h('div', { class: 'record-card-media' }, typeof img === 'string' && img ? h('img', { src: img, alt: '' }) : ini !== null ? h('span', { class: 'card-initials' }, ini) : h('span', { class: 'card-initials card-icon' }, icon(c.icon ?? this.desc.icon ?? 'file'))),
      h('div', { class: 'record-card-text' }, h('div', { class: 'record-card-title' }, title || '—'), ...(sub ? [h('div', { class: 'record-card-sub' }, String(sub))] : [])),
      ...(quick.length ? [h('div', { class: 'record-card-acts' }, quick)] : []),
    );
  }

  /** Podmínka příkazu na hodnotu záznamu (Odblokovat jen u zablokovaného…); odkaz jen když je kam. */
  private allowedCmd(c: CommandDef): boolean {
    if (c.kind === 'link' && c.href && !fillHref(c.href, this.record)) return false;
    return !c.when || c.when.values.includes(this.record[c.when.field] as string | number | boolean);
  }

  private titleText(): string {
    const base = tk(this.desc.title);
    if (this.desc.isNew) return this.desc.titleNew ? tk(this.desc.titleNew) : /^(nov[ýáé]|new)(?!\p{L})/iu.test(base) ? base : `${base} – ${t('core.ui.new_record').toLocaleLowerCase()}`;
    const key = this.desc.titleField ?? this.desc.fields[0]?.key;
    const v = key ? this.record[key] : null;
    if (v && this.bar && this.desc.card) return String(v); // záznam s vizitkou (kontakt…): jen jméno, ikona řekne, co to je
    return v ? `${base} – ${String(v)}` : base;
  }

  private build(): void {
    this.tabs.setTabs(Object.entries(this.desc.tabs).map(([id, label]) => ({ id, label: tk(label) })));
    const rows = this.desc.layout === 'rows';
    if (rows) this.el.classList.add('layout-rows');
    for (const def of this.desc.fields) {
      const fc = new FieldControl(def, rows ? 'rows' : 'stack');
      fc.setValue(this.record[def.key] ?? def.default ?? null);
      fc.onChange = () => {
        this.record[def.key] = fc.value();
        this.dirty = true;
        this.win.classList.add('dirty');
        this.applyConditions();
        fc.setError(null);
        this.saveDraft();
        this.updateChanges();
      };
      this.fields.set(def.key, fc);
      this.groupBox(def).appendChild(fc.el);
    }
    this.applyConditions();
    this.buildEmbeds();
    this.markClean();
  }

  // ---------------------------------------------------------------- změny (návrh vzhledu 3. 11.)

  /** Hodnoty při načtení / uložení – změněný řádek má pruh, Uložit a Zahodit jdou jen při změně. */
  private clean = new Map<string, string>();
  private changesEl = h('span', { class: 'rec-changes' });
  private scales: (() => void)[] = [];

  private static norm(v: unknown): string {
    return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0) ? '' : JSON.stringify(v);
  }

  /** Aktuální hodnoty polí jsou výchozí (po načtení / uložení / zahození). */
  private markClean(): void {
    queueMicrotask(() => {
      this.clean.clear();
      for (const [k, fc] of this.fields) this.clean.set(k, RecordView.norm(fc.value()));
      this.updateChanges();
    });
  }

  /** Počet změněných polí; pruh u řádku, text v záhlaví, Uložit / Zahodit povolené jen při změně. */
  private changeCount = 0;
  private updateChanges(): void {
    let n = 0;
    for (const [k, fc] of this.fields) {
      const changed = !fc.def.readonly && RecordView.norm(fc.value()) !== (this.clean.get(k) ?? '');
      fc.el.classList.toggle('changed', changed && this.edit);
      if (changed) n++;
    }
    for (const s of this.scales) s();
    const before = this.changeCount;
    this.changeCount = this.edit ? n : 0;
    // krátce (● 2), celý text v bublině – v úzkém okně nesmí vytlačit Uložit
    this.changesEl.textContent = this.changeCount > 0 ? String(this.changeCount) : '';
    this.changesEl.title = this.changeCount > 0 ? t('core.ui.changes_n', { n: this.changeCount }) : '';
    if ((before > 0) !== (this.changeCount > 0)) this.buildRibbon();
  }

  /** Uložit / Zahodit jdou jen se změnou (nový záznam vždy – vyplněné výchozí hodnoty se ukládají). */
  private get noChanges(): boolean {
    return !this.desc.isNew && this.changeCount === 0;
  }

  /** Stupnice bloku (barevný pruh): úseky podle hodnot polí, překreslí se při změně (Field::scale). */
  private scaleBar(def: FieldDef): HTMLElement {
    const bar = h('div', { class: 'fscale-bar' });
    const legend = h('div', { class: 'fscale-legend' });
    const max = def.scaleMax ?? 100;
    const seg = (cls: string, pct: number): HTMLElement => { const e = h('span', { class: cls }); e.style.width = pct + '%'; return e; };
    const paint = (): void => {
      const stops = (def.scale ?? []).map((s) => ({ ...s, v: Number(this.fields.get(s.field)?.value() ?? this.record[s.field] ?? 0) || 0 }));
      let from = 0;
      const segs: HTMLElement[] = [];
      const labels: HTMLElement[] = [h('span', { class: 'tone-ok' }, t('core.ui.scale_ok'))];
      for (const s of stops) {
        const to = Math.min(max, Math.max(from, s.v));
        segs.push(seg('seg tone-' + (segs.length === 0 ? 'ok' : stops[segs.length - 1]?.tone ?? 'ok'), ((to - from) / max) * 100)); // šířka přes CSSOM (CSP nepovolí atribut style)
        labels.push(h('span', { class: 'tone-' + s.tone }, tk(s.label, { v: s.v })));
        from = to;
      }
      segs.push(seg('seg tone-' + (stops[stops.length - 1]?.tone ?? 'bad'), ((max - from) / max) * 100));
      bar.replaceChildren(...segs);
      legend.replaceChildren(...labels);
    };
    this.scales.push(paint);
    queueMicrotask(paint);
    return h('div', { class: 'fscale' }, bar, legend);
  }

  /** Vložená okna v záložkách: načtou se až při prvním otevření záložky (popis okna = jeho vlastní kontrola oprávnění). */
  private embedded = new Map<string, { destroy?: () => void }>();
  private buildEmbeds(): void {
    const embeds = this.desc.embeds ?? [];
    if (!embeds.length) return;
    const open = (tab: string): void => {
      const e = embeds.find((x) => x.tab === tab);
      if (!e || this.embedded.has(tab)) return;
      const panel = this.tabs.panel(tab);
      panel.classList.add('record-embed');
      const ld = loader();
      panel.appendChild(ld.el);
      this.embedded.set(tab, {});
      void action<WindowDescriptor>(e.app, e.window, 'describe', e.params)
        .then((d) => {
          const view = createView(d, this.win, this.wm, { close: () => undefined, result: () => undefined, inline: true, embedded: true });
          ld.remove();
          panel.appendChild(view.el);
          this.embedded.set(tab, { destroy: () => view.destroy?.() });
        })
        .catch((err: ApiError) => {
          ld.remove();
          this.embedded.delete(tab);
          panel.appendChild(errorState(tk(err.message, err.params ?? undefined), () => { panel.replaceChildren(); open(tab); }));
        });
    };
    const prev = this.tabs.onChange;
    this.tabs.onChange = (id) => { prev?.(id); open(id); };
    open(this.tabs.current());
  }

  private applyConditions(): void {
    for (const def of this.desc.fields) {
      if (def.visibleWhen) this.fields.get(def.key)?.setVisible(evalCondition(def.visibleWhen, this.record));
    }
    queueMicrotask(() => this.markEmptyTabs());
    // blok bez viditelného pole se schová
    for (const g of this.el.querySelectorAll<HTMLElement>('.field-group')) g.hidden = !Array.from(g.querySelectorAll<HTMLElement>('.field-group-body > .field')).some((f) => !f.hidden);
  }

  /** Kam pole patří: blok s nadpisem (Field::group – po sobě jdoucí pole stejného bloku), jinak rovnou záložka. */
  private lastGroup = new Map<string, { key: string; body: HTMLElement }>();
  private groupBox(def: FieldDef): HTMLElement {
    const panel = this.tabs.panel(def.tab);
    const rows = this.desc.layout === 'rows';
    if (!def.group && !rows) {
      this.lastGroup.delete(def.tab);
      return panel;
    }
    const key = def.group ?? '';
    const last = this.lastGroup.get(def.tab);
    if (last && last.key === key) return last.body;
    if (rows) { // řádky nastavení: nadpis bloku nad kartou, řádky v kartě (pole bez bloku v kartě bez nadpisu)
      const body = h('div', { class: 'field-group-body fcard' });
      const scale = def.scale?.length ? this.scaleBar(def) : null;
      panel.appendChild(h('section', { class: 'field-group fgroup' }, def.group ? h('div', { class: 'fgroup-head' }, h('h3', null, tk(def.group)), def.groupHelp ? h('p', null, tk(def.groupHelp)) : null) : null, scale, body));
      this.lastGroup.set(def.tab, { key, body });
      return body;
    }
    if (!def.group) return panel;
    const body = h('div', { class: 'field-group-body' });
    panel.appendChild(h('fieldset', { class: 'field-group' }, h('legend', { class: 'field-group-title' }, tk(def.group)), def.groupHelp ? h('div', { class: 'field-group-help' }, tk(def.groupHelp)) : null, body));
    this.lastGroup.set(def.tab, { key: def.group, body });
    return body;
  }

  private buildRibbon(): void {
    const buttons: RibbonButton[] = [];
    if (this.desc.readonly) {
      // jen pro čtení (Zdraví, přehledy): žádné Upravit / Uložit
    } else if (this.edit) {
      const off = this.noChanges;
      buttons.push({ id: '__save', label: t('core.cmd.save'), icon: 'save', shortcut: 'Ctrl+S', group: 'core.grp.edit', primary: true, disabled: off, onClick: () => void this.save(true) });
      if (this.desc.layout !== 'rows') buttons.push({ id: '__apply', label: t('core.cmd.apply'), icon: 'check', group: 'core.grp.edit', disabled: off, onClick: () => void this.save(false) });
      buttons.push({ id: '__cancel', label: t('core.cmd.cancel'), icon: 'x', shortcut: 'Esc', group: 'core.grp.edit', disabled: off && this.desc.layout === 'rows', onClick: () => void this.cancel() });
    } else {
      buttons.push({ id: '__edit', label: t('core.cmd.edit'), icon: 'edit', shortcut: 'F2', group: 'core.grp.edit', primary: true, onClick: () => this.setEdit(true) });
    }
    const allowed = (c: CommandDef): boolean => this.allowedCmd(c);
    // akce záznamu jen v režimu čtení uloženého záznamu (při úpravě a u nového záznamu jen Uložit / Zahodit – kontrola 3. 11.)
    if (!this.edit && this.id && !this.desc.isNew) {
      for (const c of this.desc.commands) {
        if (this.bar && !allowed(c)) continue; // co teď nejde (Odblokovat bez blokace…), se neukáže
        buttons.push({ id: c.id, label: tk(c.label), icon: c.icon, shortcut: c.shortcut, tab: c.tab, group: c.group, danger: c.danger, disabled: !allowed(c), onClick: () => void this.execute(c) });
      }
    }
    if (this.desc.canDelete && this.id && !this.edit) { // Upravit · akce · Smazat; při úpravě jen Uložit · Použít · Zahodit (vzor, bod F1)
      buttons.push({ id: '__delete', label: tk(this.desc.deleteLabel ?? 'core.cmd.delete'), icon: 'trash', group: 'core.grp.manage', danger: true, onClick: () => void this.remove() });
    }
    // kontextové AI akce okna (kap. 34): návrh se jen nabídne, do pole zapíše až uživatel
    for (const a of this.desc.aiActions ?? []) {
      buttons.push({ id: 'ai_' + a.key, label: tk(a.label), icon: a.icon || 'sparkles', group: 'core.grp.ai', onClick: () => void this.runAiAction(a) });
    }
    if (this.bar) {
      this.buildBar(buttons);
      return;
    }
    this.ribbon.setButtons(buttons);
    this.ribbon.setMode(this.wm.currentMode());
  }

  /** Ovládání 'bar': stejné příkazy jako lišta (Upravit / Uložit hlavní, ostatní karty do ⋯); rychlé akce jsou na vizitce. */
  private buildBar(buttons: RibbonButton[]): void {
    const quick = new Set(this.desc.card ? this.desc.commands.filter((c) => c.quick).map((c) => c.id) : []);
    const items: BarItem[] = buttons.filter((b) => !quick.has(b.id)).map((b) => ({
      id: b.id, label: b.label, icon: b.icon, shortcut: b.shortcut, danger: b.danger, disabled: b.disabled, onClick: b.onClick,
      place: b.primary ? 'primary' : (b.tab && b.tab !== 'home') || b.group === 'core.grp.ai' ? 'more' : 'action',
    }));
    if (this.pane && this.id) items.push({ id: '__window', label: t('core.ui.open_window'), icon: 'window', place: 'more', onClick: () => void this.wm.open({ app: this.desc.app, window: this.desc.window, params: this.desc.params, modal: true, parent: this.win }) });
    this.bar?.setItems(items);
    // akce detailu v záhlaví okna seznamu (jedno záhlaví, postup 64); rychlé akce vizitky, ať je lišta seznamu nezdvojí
    if (this.pane) this.host.paneItems?.(items, buttons.filter((b) => quick.has(b.id)).map((b) => b.label));
    this.renderCard();
    this.renderHeader();
  }

  /** Samostatné okno záznamu s ovládáním 'bar': lišta v záhlaví, na telefonu vlevo ← (zavřít). */
  private renderHeader(): void {
    if (!this.bar || this.pane) return;
    if (this.wm.currentMode() !== 'mobile') {
      this.win.setHeader({ start: [], main: [this.changesEl, this.bar] });
      return;
    }
    const items = this.bar.all();
    const main = items.find((b) => b.place === 'primary' && !b.disabled);
    const rest = items.filter((b) => b !== main && !b.disabled);
    const menu = (): MenuItem[] => [
      ...rest.map(barMenuItem),
      ...(rest.length ? [{ separator: true, label: '' }] : []),
      { label: t('core.ui.minimize'), icon: 'minimize', onSelect: () => this.win.minimize() },
      { label: t('core.ui.close_window'), icon: 'close', onSelect: () => void this.win.close() },
    ];
    this.win.setHeader({
      start: [headButton('arrow-left', t('core.ui.back'), () => void this.win.close())],
      main: [
        h('span', { class: 'head-spacer' }),
        ...(main ? [headButton(main.id === '__save' ? 'check' : main.icon ?? 'edit', main.label, () => main.onClick?.())] : []),
        headButton('more-vertical', t('core.ui.menu'), (b) => IwMenu.showAt(menu(), b)),
      ],
    });
  }

  private setSaving(on: boolean): void {
    if (!this.bar) {
      this.ribbon.update('__save', { disabled: on });
      return;
    }
    const it = this.bar.all().find((b) => b.id === '__save');
    if (it) {
      it.disabled = on;
      this.bar.setItems(this.bar.all());
      if (this.pane) this.host.paneItems?.(this.bar.all());
      else this.renderHeader();
    }
  }

  private async execute(cmd: CommandDef): Promise<void> {
    if (cmd.kind === 'link' && cmd.href) {
      const url = fillHref(cmd.href, this.record);
      if (url) window.open(url, /^https?:/.test(url) ? '_blank' : '_self', 'noopener,noreferrer');
      return;
    }
    if (cmd.kind === 'open' && cmd.opens) {
      const params: Record<string, unknown> = {};
      for (const [k, src] of Object.entries(cmd.opens.params)) params[k] = src.startsWith('=') ? src.slice(1) : this.record[src];
      await this.wm.open({ app: cmd.opens.app ?? this.desc.app, window: cmd.opens.window, params, modal: cmd.opens.modal, parent: this.win });
      return;
    }
    if (cmd.confirm && !(await confirm(tk(cmd.confirm, { count: 1 }), { danger: cmd.danger }))) return;
    try {
      const { runCommand } = await import('../api');
      const res = await runCommand(this.desc.app, this.desc.window, this.desc.params, cmd.id, this.id ? [this.id] : [], this.record);
      if (res.toast) toast(tk(res.toast.text, res.toast.params), res.toast.kind);
      if (res.refresh) await this.reload();
      if (res.close) this.host.close(res.result);
      events.kick();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  private setEdit(edit: boolean): void {
    this.edit = edit;
    queueMicrotask(() => this.updateChanges());
    this.applyEdit();
    this.buildRibbon();
    if (edit) this.focus();
  }

  private applyEdit(): void {
    this.el.classList.toggle('editing', this.edit);
    for (const fc of this.fields.values()) fc.setEdit(this.edit);
    queueMicrotask(() => this.markEmptyTabs());
  }

  /** Záložka, ve které v režimu čtení nic není (prázdná pole se schovávají): text „Nic není vyplněno“. */
  private markEmptyTabs(): void {
    for (const id of Object.keys(this.desc.tabs)) {
      const panel = this.tabs.panel(id);
      panel.querySelector(':scope > .tab-empty')?.remove();
      if (this.edit || !this.el.classList.contains('hide-empty') || (this.desc.embeds ?? []).some((e) => e.tab === id)) continue;
      const fields = Array.from(panel.querySelectorAll<HTMLElement>(':scope > .field'));
      if (fields.length && fields.every((f) => f.hidden || f.classList.contains('is-empty'))) panel.prepend(h('div', { class: 'tab-empty muted' }, t('core.ui.tab_empty')));
    }
  }

  shortcut(sc: string): boolean {
    if (sc === 'Ctrl+S' && this.edit) {
      void this.save(true);
      return true;
    }
    if (sc === 'F2' && !this.edit && !this.desc.readonly) {
      this.setEdit(true);
      return true;
    }
    if (sc === 'Esc' && this.edit && !this.win.modal) {
      void this.cancel();
      return true;
    }
    if (sc === 'Esc' && this.edit && this.win.modal) {
      void this.cancel();
      return true;
    }
    const cmd = this.desc.commands.find((c) => c.shortcut === sc);
    if (cmd && !this.edit && this.id && !this.desc.isNew && this.allowedCmd(cmd)) {
      void this.execute(cmd);
      return true;
    }
    return false;
  }

  private collect(): Record<string, unknown> {
    const data: Record<string, unknown> = {};
    for (const [k, fc] of this.fields) {
      if (fc.def.readonly) continue;
      if (fc.def.visibleWhen && !evalCondition(fc.def.visibleWhen, this.record)) continue;
      data[k] = fc.value();
    }
    return data;
  }

  private validateLocal(): boolean {
    let ok = true;
    let first: FieldControl | null = null;
    for (const fc of this.fields.values()) {
      if (fc.el.hidden) continue;
      const err = fc.check();
      fc.setError(err);
      if (err) {
        ok = false;
        first ??= fc;
      }
    }
    if (first) {
      this.tabs.select(first.def.tab);
      first.focus();
    }
    return ok;
  }

  async save(closeAfter: boolean): Promise<void> {
    if (this.saving) return;
    if (!this.validateLocal()) return;
    this.saving = true;
    this.setSaving(true);
    try {
      const res = await action<{ id: string; record: Record<string, unknown>; canDelete?: boolean }>(this.desc.app, this.desc.window, 'save', this.desc.params, { data: this.collect(), version: this.record['version'] ?? null });
      this.record = { ...res.record };
      if (res.canDelete !== undefined) this.desc.canDelete = res.canDelete; // právo smazat podle uloženého záznamu
      this.id = res.id;
      this.dirty = false;
      this.win.classList.remove('dirty');
      this.markClean();
      this.saveDraft.cancel();
      void core('drafts', 'delete', { key: this.draftKey }).catch(() => undefined);
      this.notice?.remove();
      this.notice = null;
      for (const [k, fc] of this.fields) fc.setValue(this.record[k] ?? null);
      events.kick();
      toast(t('core.ui.saved'), 'ok');
      this.host.result(res.id);
      if (closeAfter && this.closesWhenDone()) {
        this.host.close(res.id);
        return;
      }
      if (this.desc.isNew) {
        // nový záznam: okno dál pracuje s uloženým záznamem
        this.desc = { ...this.desc, isNew: false, params: { ...this.desc.params, id: res.id } };
        this.win.params = this.desc.params;
        this.draftKey = `${this.desc.app}/${this.desc.window}/${res.id}`;
      }
      this.setWinTitle();
      if (closeAfter) this.setEdit(false);
      else this.renderCard();
    } catch (e) {
      const err = e as ApiError;
      if (err.fields) {
        let first: FieldControl | null = null;
        for (const [k, msg] of Object.entries(err.fields)) {
          const fc = this.fields.get(k);
          fc?.setError(msg);
          first ??= fc ?? null;
        }
        if (first) {
          this.tabs.select(first.def.tab);
          first.focus();
        }
      } else toastError(err);
    } finally {
      this.saving = false;
      this.setSaving(false);
    }
  }

  private async cancel(): Promise<void> {
    if (this.dirty && !(await confirmDiscard())) return;
    this.saveDraft.cancel();
    void core('drafts', 'delete', { key: this.draftKey }).catch(() => undefined);
    this.notice?.remove(); // lišta „obnoveny neuložené změny“ po zahození nemá zůstat
    this.notice = null;
    if (this.desc.isNew || this.closesWhenDone()) {
      this.host.close();
      return;
    }
    await this.reload();
    this.dirty = false;
    this.win.classList.remove('dirty');
      this.markClean();
    this.setEdit(false);
  }

  /** Okno otevřené jako detail (z lišty / dialog): Uložit a Zahodit ho zavřou. Podokno seznamu nikdy. */
  private closesWhenDone(): boolean {
    return !this.pane && (this.win.modal || this.win.closeOnSave);
  }

  private async reload(): Promise<void> {
    if (!this.id) return;
    try {
      const res = await action<{ record: Record<string, unknown>; canDelete?: boolean }>(this.desc.app, this.desc.window, 'load', this.desc.params);
      this.record = { ...res.record };
      if (res.canDelete !== undefined) this.desc.canDelete = res.canDelete;
      for (const [k, fc] of this.fields) fc.setValue(this.record[k] ?? null);
      this.applyConditions();
      this.setWinTitle();
      this.renderCard();
      this.buildRibbon();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  private async remove(): Promise<void> {
    if (!this.id || !(await confirm(t(this.desc.deleteConfirm ?? 'core.cmd.confirm_delete', { count: 1 }), { danger: true, okLabel: t('core.cmd.delete') }))) return;
    try {
      await action(this.desc.app, this.desc.window, 'delete', this.desc.params);
      events.kick();
      toast(t('core.ui.deleted'), 'ok');
      this.dirty = false;
      this.host.close('deleted');
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  // ---- koncepty

  private async persistDraft(): Promise<void> {
    if (!this.dirty) return;
    try {
      await core('drafts', 'save', { key: this.draftKey, title: this.titleText(), state: { record: this.collect() } });
    } catch {
      /* koncept není kritický */
    }
  }

  private async loadDraft(): Promise<void> {
    try {
      const res = await core<{ state: { record?: Record<string, unknown> } | null; updatedAt?: string | null }>('drafts', 'load', { key: this.draftKey });
      if (res.state?.record) {
        if (!this.edit) {
          this.pendingDraft = res.state.record; // otevřeno ke čtení (podokno i samostatné okno): jen nabídnout, úpravy až na pokyn
          this.showDraftNotice(res.updatedAt ?? null);
          return;
        }
        this.applyDraft(res.state.record);
        this.showDraftNotice(res.updatedAt ?? null);
      }
    } catch {
      /* ignore */
    }
  }

  private applyDraft(rec: Record<string, unknown>): void {
    Object.assign(this.record, rec);
    for (const [k, fc] of this.fields) if (k in rec) fc.setValue(rec[k] ?? null);
    this.dirty = true;
    this.win.classList.add('dirty');
    if (!this.edit) this.setEdit(true);
    this.applyConditions();
  }

  /** Místo pouhého oznámení: lišta s datem rozpracování a tlačítkem Zahodit (rozhodnutí 25. 10. 2026); v náhledu i Pokračovat. */
  private showDraftNotice(at: string | null): void {
    this.notice?.remove();
    const btn = h('button', { type: 'button', class: 'btn' }, t('core.ui.draft_discard'));
    btn.addEventListener('click', () => void this.discardDraft());
    const date = at ? fmtDateTime(at) : '';
    let resume: HTMLElement | null = null;
    if (this.pendingDraft) {
      resume = h('button', { type: 'button', class: 'btn primary' }, t('core.ui.draft_resume'));
      resume.addEventListener('click', () => {
        const rec = this.pendingDraft;
        this.pendingDraft = null;
        if (rec) this.applyDraft(rec);
        this.showDraftNotice(at);
      });
    }
    this.notice = h('div', { class: 'record-notice' }, icon('info'), h('span', null, t(resume ? 'core.ui.draft_pending' : 'core.ui.draft_restored', { date })), resume, btn);
    this.tabs.before(this.notice);
  }

  /** Zahodit rozpracované: smazat koncept a vrátit pole na uložený záznam (u nového na výchozí hodnoty). */
  private async discardDraft(): Promise<void> {
    this.pendingDraft = null;
    this.saveDraft.cancel();
    this.notice?.remove();
    this.notice = null;
    void core('drafts', 'delete', { key: this.draftKey }).catch(() => undefined);
    this.record = { ...this.desc.record };
    for (const [k, fc] of this.fields) {
      fc.setValue(this.record[k] ?? fc.def.default ?? null);
      fc.setError(null);
    }
    this.dirty = false;
    this.win.classList.remove('dirty');
      this.markClean();
    this.applyConditions();
    if (!this.desc.isNew && this.edit) this.setEdit(false);
    this.setWinTitle();
  }

  async canClose(): Promise<boolean> {
    if (!this.dirty) return true;
    if (!(await confirm(t('core.ui.unsaved'), { danger: true, okLabel: t('core.ui.discard'), cancelLabel: t('core.ui.keep_editing') }))) return false;
    // Zahodit změny = i rozpracovaný koncept pryč (jinak by se úprava při dalším otevření „obnovila“)
    this.saveDraft.cancel();
    this.dirty = false;
    this.win.classList.remove('dirty');
      this.markClean();
    void core('drafts', 'delete', { key: this.draftKey }).catch(() => undefined);
    return true;
  }

  setMode(mode: 'pc' | 'tablet' | 'mobile'): void {
    if (this.bar) this.renderHeader();
    else this.ribbon.setMode(mode);
  }

  getState(): unknown {
    return { tab: this.tabs.current(), edit: this.edit };
  }

  setState(state: unknown): void {
    const s = state as { tab?: string; edit?: boolean; prefill?: Record<string, unknown> } | null;
    if (s?.tab) this.tabs.select(s.tab);
    if (s?.edit && !this.edit && !this.desc.readonly) this.setEdit(true);
    if (s?.prefill && typeof s.prefill === 'object') {
      // předvyplnění (asistent, odkazy): hodnoty se jen nabídnou ve formuláři, uložit musí uživatel
      for (const [k, v] of Object.entries(s.prefill)) {
        const fc = this.fields.get(k);
        if (!fc) continue;
        fc.setValue(v);
        this.record[k] = fc.value();
      }
      this.dirty = true;
      this.win.classList.add('dirty');
      if (!this.edit) this.setEdit(true);
      this.applyConditions();
    }
  }

  private async runAiAction(a: import('../contract').AiActionDef): Promise<void> {
    const target = a.target ? this.fields.get(a.target) : null;
    const canWrite = !!target && this.edit && !target.def.readonly;
    const original = target ? String(target.value() ?? '') : '';
    const picked = await aiSuggest(tk(a.label), original, async () => {
      const r = await action<{ text: string; online: boolean; target: string | null; mode: string }>(this.desc.app, this.desc.window, 'ai', this.desc.params, { action: a.key, record: { ...this.record, ...this.collect() } });
      return { text: r.text, online: r.online, provider: '' };
    }, canWrite);
    if (!picked || !target) return;
    const value = picked.mode === 'replace' || !original ? picked.text : original.replace(/\s*$/, '') + '\n\n' + picked.text;
    target.setValue(value);
    this.record[target.def.key] = value;
    this.dirty = true;
    this.win.classList.add('dirty');
    this.saveDraft();
  }

  focus(): void {
    if (!this.edit) return;
    const own = this.desc.fields.filter((f) => !f.readonly && f.tab === this.tabs.current() && !this.fields.get(f.key)?.el.hidden);
    const empty = (f: FieldDef): boolean => { const v = this.fields.get(f.key)?.value(); return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0); };
    const first = own.find((f) => f.required && empty(f)) ?? own.find((f) => ['text', 'textarea', 'email', 'number', 'password', 'date', 'datetime'].includes(f.type)) ?? own[0];
    if (first) this.fields.get(first.key)?.focus();
  }

  destroy(): void {
    this.ro?.disconnect();
    this.unsubscribe?.();
    this.saveDraft.flush();
    for (const e of this.embedded.values()) e.destroy?.();
  }
}
